// Hint 컴포넌트 - 시안 ico_hint 기반
import React, { PropsWithChildren } from "react";

// 크기/색 변형이 없는 단일 형태라 Round와 달리 별도 옵션을 받지 않는다
export type HintProps = Omit<PropsWithChildren, "style">;

/**
 * 메뉴 위에 떠서 안내 문구를 보여주는 말풍선 딱지 (업무 #12091)
 *
 * - 메뉴를 누르면 무엇을 할 수 있는지 한 줄로 알려준다
 * - 문구 자체가 정보라 스크린 리더에도 그대로 읽힌다 (뉴스/디라와 동일한 판단)
 * - 붙는 자리의 레이아웃과 클릭 영역을 건드리지 않도록 absolute + pointer-events-none
 *
 * 쓰는 곳에서 지켜야 할 것:
 *   기준이 되는 부모에 position: relative가 있어야 한다. 가로 중앙 정렬과
 *   세로 위치(메뉴명 위)는 이 컴포넌트가 직접 잡는다.
 *
 * 크기는 시안이 pc/m 두 벌을 정의하는데, 반응형 props를 받지 않는 프로젝트
 * 규칙에 따라 mode prop 대신 Tailwind 반응형 유틸리티로 처리한다.
 * 브레이크포인트는 기본 md(767.98px)가 아니라 max-[768px]를 쓴다 —
 * 헤더의 emotion 미디어쿼리와 isMobile 판정이 모두 768px 이하 기준이라
 * md를 쓰면 정확히 768px에서 마크업은 모바일인데 딱지만 PC 크기로 어긋난다.
 */
function Hint({ children = "Label" }: HintProps) {
  return (
    <em
      data-component="BadgeHint"
      className={[
        // 배치: 기준 부모의 가운데 위. 시안상 딱지 하단 ~ 메뉴 텍스트 박스 상단 3px(m 2px)
        "absolute left-1/2 z-[5] -translate-x-1/2",
        "bottom-[calc(100%+3px)] max-[768px]:bottom-[calc(100%+2px)]",
        // 딱지가 메뉴 클릭을 가로채지 않게. em 기본 italic도 해제한다
        "pointer-events-none whitespace-nowrap not-italic",
        "flex items-center justify-center rounded-[100px] bg-[#33ACD2]",
        // 본문 (시안 pc / m)
        "px-[9px] py-[5px] text-[11px] leading-[11px] tracking-[-0.22px]",
        "max-[768px]:px-[7px] max-[768px]:py-[3px]",
        "max-[768px]:text-[10px] max-[768px]:leading-[10px] max-[768px]:tracking-[-0.2px]",
        // 서브셋해 self-host한 실제 Noto Sans KR Bold. 문구를 바꿀 때 지켜야 할
        // 주의사항은 scss/common/common.scss의 @font-face 주석에 적어뒀다
        "font-notohint font-bold text-white",
        // 시안은 box-shadow가 아닌 drop-shadow 필터다. box-shadow는 둥근 본체에만
        // 걸려 꼬리가 그림자 없이 납작하게 떠 보이지만, 필터는 꼬리까지 실루엣을
        // 따라 감싼다. 농도는 시안이 mode별로 다르게 준다 (pc 0.38 / m 0.2)
        "drop-shadow-[0_2px_3.5px_rgba(51,172,210,0.38)]",
        "max-[768px]:drop-shadow-[0_2px_3.5px_rgba(51,172,210,0.2)]",
        // 말풍선 꼬리. 아래를 향하는 삼각형이라 border-bottom은 0 (시안 pc 10x5 / m 6x3)
        // content는 Tailwind가 after: 변형에 기본값 ''로 넣어준다
        "after:absolute after:left-1/2 after:top-full after:-translate-x-1/2",
        "after:border-solid after:border-b-0 after:border-x-transparent after:border-t-[#33ACD2]",
        "after:border-x-[5px] after:border-t-[5px]",
        "max-[768px]:after:border-x-[3px] max-[768px]:after:border-t-[3px]",
        // 2.6초 주기 3px 바운스 2회 후 정지 (업무 참고안 wsb-loop, 뉴스/디라와 동일 값).
        // motion-safe라 prefers-reduced-motion에서는 애니메이션 자체가 붙지 않는다
        "motion-safe:animate-badge-hint-bounce",
      ].join(" ")}
    >
      {children}
    </em>
  );
}

export default Hint;
